import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { isEmpty } from 'underscore';
import moment from 'moment';
// There is a dependancy cycle here from FormSection importing common-logic
/* eslint-disable-next-line import/no-cycle */
import { FormSection } from 'AppSrc/core/components/forms/form-section';
import { prefixStringWithIndexPlusOne } from 'AppSrc/core/utility-functions/string-fun';
import {
  formDataShape,
} from 'AppSrc/core/components/forms/form-base-components/data-structure/v-f-proptypes';
import { COORDINATOR } from 'AppSrc/core/components/forms/form-base-components/data-structure/constants';
import { navigationPropsShape } from 'AppSrc/core/components/forms/section-nav';
import { userRolesPropShape } from 'AppSrc/core/auth/auth-proptypes';
import {
  r1Yes,
  r2No,
  r5No,
  r6No,
  // r11No,
  preImp6No,
  MOBILE,
  FIXED,
  FIXEDOLO,
  NO_OUTAGE,
  EXPEDITED_MOBILE,
  EXPEDITED_FIXED,
  NORMAL_MOBILE,
  NORMAL_FIXED,
  EMERGENCY_MOBILE,
  EMERGENCY_FIXED,
  // ZERO_MIN_BUS_CRITICAL_UP_TO_1HR_NON,
} from 'AppSrc/core/components/forms/value-dependency-constants';
import { getDisplayOptionsToUse } from 'AppSrc/core/components/forms/data/get-display-options-to-use';
// Key values that are repeated a lot
import { defaultFieldLogic } from 'AppSrc/core/components/forms/form-base-components/data-structure/default-field-logic';
// common logic across this form
// common logic needs to be shared between sections but it also needs to know the id of some sections. This causes a
// cycle dependancy but as it's just a string, it's not really a problem!
/* eslint-disable-next-line import/no-cycle */
import {
  currentSelectedNetwork,
  userRoleForSelectedNetwork,
  getIsOlaCrq,
  isCrqExpedited,
} from 'AppSrc/core/components/forms/form-types/create-crq/common-logic';
import {
  CHANGE_QUESTIONS,
  SECTION_TITLES,
} from 'AppSrc/core/components/forms/form-types/create-crq/form-definition/section-constants';

// form fields
// expedited questions
import { whyShouldThisChangeBeExpedited } from './why-should-this-change-be-expedited';
import { businessJustificationA } from './business-justification-a';
import { businessJustificationB } from './business-justification-b';
import { businessJustificationC } from './business-justification-c';
// impact level (FIXED ONLY)
import { impact } from './impact';
// risk questions MOBILE
import { firstSiteImplementationParams } from './first-site-implementation-params';
import { firstSiteImplementationBParams } from './first-site-implementation-b-params';
import { testingStatus } from './testing-status';
import { acceptanceSignOff } from './acceptance-sign-off';
import { isRegressionPossible } from './is-regression-possible';
import { regressionPlanTested } from './regression-plan-tested';
import { changeScopeTechnology } from './change-scope-technology';
import { changeScopeDomain } from './change-scope-domain';
import { cisAffectedResilient } from './cis-affected-resilient';
import { serviceOutageRequired } from './service-outage-required';
import { serviceOutageRequiredForRegressionActivity } from './service-outage-required-for-regression-activity';
import { serviceOutageDuration } from './service-outage-duration';
// risk questions FIXED
import { failureBeBusinessCritical } from './failure-be-business-critical';
import { completeBackoutEasilyAchievable } from './complete-backout-easily-achievable';
import { whatTestingHasBeenCarriedOut } from './what-testing-has-been-carried-out';
import { newVersionsOfSoftware } from './new-versions-of-software';
import { newProductTechnology } from './new-product-technology';
import { vendorSupportForChange } from './vendor-support-for-change';
import { changeMultiSiteMultiCustomerAffecting } from './change-multi-site-multi-customer-affecting';
import { implementersImplementedThisChangeBefore } from './implementers-implemented-this-change-before';
// pre-implementation questions
import { backoutAndTestCompleted } from './backout-and-test-completed';
import { customerImpacting } from './customer-impacting';
import { affectsInventoryCmdbOss } from './affects-inventory-cmdb-oss';
import { provideRequiredLeadTime } from './provide-required-lead-time';
import { requiresPreChangeLabTestingResults } from './requires-pre-change-lab-testing-results';
import { requiresNetworkItSecurityApproval } from './requires-network-it-security-approval';
import { changeImpactTechnologyRecoverPlan } from './change-impact-technology-recovery-plans';
import { changeImpactTechnologyRecoverPlanFixed } from './change-impact-technology-recovery-plans-fixed';
import { whyNoNetworkItSecurityApproval } from './why-no-network-it-security-approval';

// sub section titles
import { sstTimingReason } from './sst-timing-reason';
import { sstImpactLevel } from './sst-impact-level';
import { sstBusinessJustification } from './sst-business-justification';
import { sstRiskQuestionnaire } from './sst-risk-questionnaire';
import { sstPreImplentationQuestionnaire } from './sst-pre-implementation-questionnaire';
import { sstSelectChangeSla } from './sst-select-change-sla';
import { selectChangeSla } from './select-change-sla';
import { NoServiceOutageDuration } from './no-outage';
import { whyNoChangeInTechnologyRecoveryPlans } from './why-no-change-in-technology-recovery-plans';
import { whyNoChangeInTechnologyRecoveryPlansFixed } from './why-no-change-in-technology-recovery-plans-fixed';
import { holidaysList } from '../section-three/section-three-data-layer-functions';

// Section ID (for the data layer)
const sectionId = CHANGE_QUESTIONS;
// Title for the main form
const sectionTitle = SECTION_TITLES[CHANGE_QUESTIONS];

// There are a handfull of SLAs to choose from and the order they are stored in display-options.json is not
// the order vodafone wants them. Additionally vodafone only wants there to be 2 SLA types for now. As such
// we filter and sort them
const filterAndSortSlaTypes = ({
  componentData,
  selectedNetwork,
}) => {

  if (!selectedNetwork) {
    return [];
  }

  const allDisplayOptions = getDisplayOptionsToUse({
    componentData,
    selectedNetwork,
  });


  const normalDisplayOption = allDisplayOptions.find((option) => ((option.value === NORMAL_FIXED) || option.value === NORMAL_MOBILE));
  const expeditedDisplayOption = allDisplayOptions.find((option) => ((option.value === EXPEDITED_FIXED) || (option.value === EXPEDITED_MOBILE)));
  const emergencyDisplayOption = allDisplayOptions.find((option) => ((option.value === EMERGENCY_FIXED) || (option.value === EMERGENCY_MOBILE)));

  if (!normalDisplayOption || !expeditedDisplayOption || !emergencyDisplayOption) {
    // this will likely fire if selectChangeSla tries to render without it's dynamically selected display options.
    // ie, it tries to get display options for 'timing' not 'timing-mobile'
    console.warn(`Cannot find "normalDisplayOption" or "expeditedDisplayOption" for ${componentData.id}`);
  }

  const filteredDisplayOptions = [normalDisplayOption, expeditedDisplayOption, emergencyDisplayOption];

  return filteredDisplayOptions;

};

// function to get the timing field from form data
const getTimingFields = ({
  selectedNetwork,
  userRole,
  isOloCrq,
  hasCrqNumber,
  values,
}) => {
  // only coordinators can see this section and only if they have mobile selected in the first option
  const showSelectChangeSla = (userRole === COORDINATOR);
  const selectChangeSlaOWV = selectedNetwork === FIXED ? NORMAL_FIXED : NORMAL_MOBILE;
  const timingFields = [];
  const slaValue = values[selectChangeSla.id];
  // const crqIsExpedited = slaValue && (slaValue.includes(EXPEDITED_MOBILE) || slaValue.includes(EXPEDITED_FIXED));
  const crqIsEmergency = slaValue && (slaValue.includes(EMERGENCY_MOBILE) || slaValue.includes(EMERGENCY_FIXED));
  timingFields.push({
    ...defaultFieldLogic,
    componentData: sstSelectChangeSla,
    shouldRender: showSelectChangeSla && !isOloCrq,
    shouldValidate: !isOloCrq,
  });
  timingFields.push({
    ...defaultFieldLogic,
    componentData: {
      ...selectChangeSla,
      displayOptions: filterAndSortSlaTypes({
        componentData: selectChangeSla,
        selectedNetwork,
      }),
    },
    shouldRender: showSelectChangeSla && !isOloCrq,
    shouldValidate: showSelectChangeSla && !isOloCrq,
    overwriteSubmit: {
      overwrite: !showSelectChangeSla,
      overwriteValue: [selectChangeSlaOWV],
    },
    readOnly: (hasCrqNumber && crqIsEmergency) ? true : false,
  });
  return timingFields;
};

const filterAndSortImpact = ({
  componentData,
  selectedNetwork,
  isOloCrq,
  crqIsExpedited,
}) => {
  if (!selectedNetwork) {
    return [];
  }

  const allDisplayOptions = getDisplayOptionsToUse({
    componentData,
    selectedNetwork,
  });

  let normalDisplayOption = [];

  // if CRQ is Fixed and Expedited or OLO then only impact level l and 2 are allowed
  if (isOloCrq || ((selectedNetwork === 'fixed') && crqIsExpedited)) {
    const val1 = allDisplayOptions.find((option) => (option.value === '1000'));
    const val2 = allDisplayOptions.find((option) => (option.value === '2000'));
    normalDisplayOption = [val1, val2];
  }
  else {
    normalDisplayOption = allDisplayOptions;
  }
  // const expeditedDisplayOption = allDisplayOptions.find((option) => ((option.value === EXPEDITED_FIXED) || (option.value === EXPEDITED_MOBILE)));

  if (!normalDisplayOption) {
    // this will likely fire if selectChangeSla tries to render without it's dynamically selected display options.
    // ie, it tries to get display options for 'timing' not 'timing-mobile'
    console.warn(`Cannot find "normalDisplayOption" for ${componentData.id}`);
  }

  const filteredDisplayOptions = [...normalDisplayOption];


  return filteredDisplayOptions;

};

const filterAndSortservice = ({
  componentData,
  selectedNetwork,
}) => {
  if (!selectedNetwork) {
    return [];
  }

  const allDisplayOptions = getDisplayOptionsToUse({
    componentData,
    selectedNetwork,
  });
  let normalDisplayOption = [];
  // const isOloCrq = false;
  if (selectedNetwork === MOBILE) {
    const val1 = allDisplayOptions.find((option) => (option.value === 'No Outage'));
    normalDisplayOption = [val1];
  }
  else {
    normalDisplayOption = allDisplayOptions;
  }
  // const expeditedDisplayOption = allDisplayOptions.find((option) => ((option.value === EXPEDITED_FIXED) || (option.value === EXPEDITED_MOBILE)));

  if (!normalDisplayOption) {
    // this will likely fire if selectChangeSla tries to render without it's dynamically selected display options.
    // ie, it tries to get display options for 'timing' not 'timing-mobile'
    console.warn(`Cannot find "normalDisplayOption" for ${componentData.id}`);
  }

  const filteredDisplayOptions = [...normalDisplayOption];

  return filteredDisplayOptions;
};
// function to get the timing field from form data
const getImpactFields = ({
  selectedNetwork,
  userRole,
  hasCrqId,
  isOloCrq,
  values,
}) => {
  const impactFields = [];
  // currentFields.push({
  //   ...defaultFieldLogic,
  //   componentData: impact,
  //   readOnly: hasCrqId,
  // });
  const slaValue = values[selectChangeSla.id];
  const crqIsExpedited = slaValue && (slaValue.includes(EXPEDITED_MOBILE) || slaValue.includes(EXPEDITED_FIXED));

  impactFields.push({
    ...defaultFieldLogic,
    componentData: {
      ...impact,
      displayOptions: filterAndSortImpact({
        componentData: impact,
        selectedNetwork,
        readOnly: hasCrqId,
        isOloCrq,
        crqIsExpedited,
      }),
    },
  });
  return impactFields;
};


// callback for the form section to figure out what fields to render with conditional logic

// If the CRQ is marked as Expedited and the user is a coordinator then we render additional
// options. If not, we save different data.
// MOBILE VS FIXED:
// We show all questions for MOBILE but only one question for FIXED
const getExpeditedFields = (params) => {
  const {
    values,
    userRole,
    selectedNetwork,
    isOloCrq,
    isExpedited,
  } = params;

  const expeditedFields = [];
  const slaValue = values[selectChangeSla.id];
  const crqIsExpedited = slaValue && (slaValue.includes(EXPEDITED_MOBILE) || slaValue.includes(EXPEDITED_FIXED));
  // const crqIsEmergency = slaValue && (slaValue.includes(EMERGENCY_MOBILE) || slaValue.includes(EMERGENCY_FIXED));
  const showTimingReasonQuestions = (userRole === COORDINATOR) && (crqIsExpedited);
  const timingFieldvalue = (selectedNetwork === 'mobile') ? 'Customer/business need' : '1000';

  expeditedFields.push({
    ...defaultFieldLogic,
    componentData: sstTimingReason,
    shouldRender: showTimingReasonQuestions && !isOloCrq,
    shouldValidate: !isOloCrq,
  });
  expeditedFields.push({
    ...defaultFieldLogic,
    componentData: whyShouldThisChangeBeExpedited,
    shouldRender: showTimingReasonQuestions && !isOloCrq,
    shouldValidate: showTimingReasonQuestions && !isOloCrq,
    overwriteSubmit: {
      overwrite: !showTimingReasonQuestions || (isOloCrq && isExpedited),
      overwriteValue: (isOloCrq && isExpedited) ? '1000' : timingFieldvalue,
    },
  });

  // Fixed only containes timing reason
  // if (selectedNetwork === FIXED) {
  //   return expeditedFields;
  // }


  expeditedFields.push({
    ...defaultFieldLogic,
    componentData: sstBusinessJustification,
    shouldRender: showTimingReasonQuestions && !isOloCrq,
  });
  expeditedFields.push({
    ...defaultFieldLogic,
    componentData: businessJustificationA,
    shouldRender: showTimingReasonQuestions && !isOloCrq,
    shouldValidate: showTimingReasonQuestions && !isOloCrq,
    overwriteSubmit: {
      overwrite: !showTimingReasonQuestions,
      overwriteValue: null,
    },
  });
  expeditedFields.push({
    ...defaultFieldLogic,
    componentData: businessJustificationB,
    shouldRender: showTimingReasonQuestions && !isOloCrq,
    shouldValidate: showTimingReasonQuestions && !isOloCrq,
    overwriteSubmit: {
      overwrite: !showTimingReasonQuestions,
      overwriteValue: null,
    },
  });
  expeditedFields.push({
    ...defaultFieldLogic,
    componentData: businessJustificationC,
    shouldRender: showTimingReasonQuestions && !isOloCrq,
    shouldValidate: showTimingReasonQuestions && !isOloCrq,
    overwriteSubmit: {
      overwrite: !showTimingReasonQuestions,
      overwriteValue: null,
    },
  });

  return expeditedFields;
};


const getMobileRiskQuestionnaireFields = (params) => {
  const {
    values,
    noValues,
    hasCrqId,
  } = params;

  const riskQuestionnaireFields = [];
  const slaValue = values[selectChangeSla.id];
  const crqIsEmergency = slaValue && (slaValue.includes(EMERGENCY_MOBILE) || slaValue.includes(EMERGENCY_FIXED));

  riskQuestionnaireFields.push({
    ...defaultFieldLogic,
    componentData: sstRiskQuestionnaire,
  });
  riskQuestionnaireFields.push({
    ...defaultFieldLogic,
    componentData: firstSiteImplementationParams,
    shouldRender: !crqIsEmergency,
    shouldValidate: !crqIsEmergency,
    overwriteSubmit: {
      overwrite: crqIsEmergency,
      overwriteValue: 'No',
    },
  });
  riskQuestionnaireFields.push({
    ...defaultFieldLogic,
    // if firstSiteImplementationParams is set as true, this becomes disabled and set to false
    componentData: firstSiteImplementationBParams,
    shouldRender: !crqIsEmergency,
    shouldValidate: !crqIsEmergency,
    disabledFieldOptions: {
      disabled: noValues ? r2No : values[firstSiteImplementationParams.id].includes(r1Yes),
      disabledValue: [r2No],
      unDisabledValue: [],
    },
    overwriteSubmit: {
      overwrite: crqIsEmergency,
      overwriteValue: 'No',
    },
  });
  riskQuestionnaireFields.push({
    ...defaultFieldLogic,
    componentData: testingStatus,
    shouldRender: !crqIsEmergency,
    shouldValidate: !crqIsEmergency,
    overwriteSubmit: {
      overwrite: crqIsEmergency,
      overwriteValue: 'N/A:No Test Environment',
    },
  });
  riskQuestionnaireFields.push({
    ...defaultFieldLogic,
    componentData: acceptanceSignOff,
    shouldRender: !crqIsEmergency,
    shouldValidate: !crqIsEmergency,
    overwriteSubmit: {
      overwrite: crqIsEmergency,
      overwriteValue: 'No',
    },
  });
  riskQuestionnaireFields.push({
    ...defaultFieldLogic,
    componentData: isRegressionPossible,
  });
  riskQuestionnaireFields.push({
    ...defaultFieldLogic,
    // if isRegressionPossible is set to true, this becomes disabled and set to false
    componentData: regressionPlanTested,
    disabledFieldOptions: {
      disabled: noValues ? r6No : values[isRegressionPossible.id].includes(r5No),
      disabledValue: [r6No],
      unDisabledValue: [],
    },
  });
  riskQuestionnaireFields.push({
    ...defaultFieldLogic,
    componentData: changeScopeTechnology,
  });
  riskQuestionnaireFields.push({
    ...defaultFieldLogic,
    componentData: changeScopeDomain,
  });
  riskQuestionnaireFields.push({
    ...defaultFieldLogic,
    componentData: cisAffectedResilient,
  });
  riskQuestionnaireFields.push({
    ...defaultFieldLogic,
    componentData: changeImpactTechnologyRecoverPlan,
    // disabledFieldOptions: {
    //   disabled: hasCrqId ? true : false,
    //   // disabledValue: [r2No],
    //   // unDisabledValue: [],
    // },
    readOnly: hasCrqId ? true : false,
  });
  const technologyPlanRequired = 'technology-plan-required';

  const technologyPlanRequiredValue = values[technologyPlanRequired] ? values[technologyPlanRequired][0] : '';

  if (technologyPlanRequiredValue !== 'Yes') {
    riskQuestionnaireFields.push({
      ...defaultFieldLogic,
      componentData: whyNoChangeInTechnologyRecoveryPlans,
      readOnly: hasCrqId,
      shouldRender: technologyPlanRequiredValue === 'No',
    });
  }
  riskQuestionnaireFields.push({
    ...defaultFieldLogic,
    componentData: serviceOutageRequired,
  });
  riskQuestionnaireFields.push({
    ...defaultFieldLogic,
    componentData: serviceOutageRequiredForRegressionActivity,
  });
  const abc = values.service_outage_required_for_regression_activity ? values.service_outage_required_for_regression_activity[0] : '';
  const cba = values.service_outage_required ? values.service_outage_required[0] : '';
  if (abc === 'No' && cba === 'No') {
    riskQuestionnaireFields.push({
      ...defaultFieldLogic,
      componentData: {
        ...NoServiceOutageDuration,
        displayOptions: filterAndSortservice({
          componentData: NoServiceOutageDuration,
          selectedNetwork: MOBILE,
        }),
      },
      disabledFieldOptions: {
        disabled: false,
        displayOptions: NO_OUTAGE,
      },
    });
  }
  else {
    riskQuestionnaireFields.push({
      ...defaultFieldLogic,
      componentData: serviceOutageDuration,
      disabledFieldOptions: {
        disabled: false,
        // disabled: noValues ? false : values[serviceOutageRequiredForRegressionActivity.id].includes(r11No),
        // NOTE! This can silently break the summary page due to typos
        // Once we have a better understanding of the specification we may need to do a
        // #disabledValueRefactor
        disabledValue: NO_OUTAGE,
        unDisabledValue: null,
      },
    });
  }
  return riskQuestionnaireFields;
};


const getFixedRiskQuestionnaireFields = (params) => {
  const {
    isOloCrq,
    values,
    // noValues,
    hasCrqId,
  } = params;

  const riskQuestionnaireFields = [];

  riskQuestionnaireFields.push({
    ...defaultFieldLogic,
    componentData: sstRiskQuestionnaire,
    shouldRender: !isOloCrq,
    shouldValidate: !isOloCrq,
  });
  riskQuestionnaireFields.push({
    ...defaultFieldLogic,
    componentData: failureBeBusinessCritical,
    shouldRender: !isOloCrq,
    shouldValidate: !isOloCrq,
    overwriteSubmit: {
      overwrite: isOloCrq,
      overwriteValue: '5',
    },
  });
  riskQuestionnaireFields.push({
    ...defaultFieldLogic,
    componentData: completeBackoutEasilyAchievable,
    shouldRender: !isOloCrq,
    shouldValidate: !isOloCrq,
    overwriteSubmit: {
      overwrite: isOloCrq,
      overwriteValue: '1',
    },
  });
  riskQuestionnaireFields.push({
    ...defaultFieldLogic,
    componentData: whatTestingHasBeenCarriedOut,
    shouldRender: !isOloCrq,
    shouldValidate: !isOloCrq,
    overwriteSubmit: {
      overwrite: isOloCrq,
      overwriteValue: '1',
    },
  });
  riskQuestionnaireFields.push({
    ...defaultFieldLogic,
    componentData: newVersionsOfSoftware,
    shouldRender: !isOloCrq,
    shouldValidate: !isOloCrq,
    overwriteSubmit: {
      overwrite: isOloCrq,
      overwriteValue: '1',
    },
  });
  riskQuestionnaireFields.push({
    ...defaultFieldLogic,
    componentData: newProductTechnology,
    shouldRender: !isOloCrq,
    shouldValidate: !isOloCrq,
    overwriteSubmit: {
      overwrite: isOloCrq,
      overwriteValue: '1',
    },
  });
  riskQuestionnaireFields.push({
    ...defaultFieldLogic,
    componentData: vendorSupportForChange,
    shouldRender: !isOloCrq,
    shouldValidate: !isOloCrq,
    overwriteSubmit: {
      overwrite: isOloCrq,
      overwriteValue: '2',
    },
  });
  riskQuestionnaireFields.push({
    ...defaultFieldLogic,
    componentData: changeMultiSiteMultiCustomerAffecting,
    shouldRender: !isOloCrq,
    shouldValidate: !isOloCrq,
    overwriteSubmit: {
      overwrite: isOloCrq,
      overwriteValue: '5',
    },
  });
  riskQuestionnaireFields.push({
    ...defaultFieldLogic,
    componentData: implementersImplementedThisChangeBefore,
    shouldRender: !isOloCrq,
    shouldValidate: !isOloCrq,
    overwriteSubmit: {
      overwrite: isOloCrq,
      overwriteValue: '1',
    },
  });
  riskQuestionnaireFields.push({
    ...defaultFieldLogic,
    componentData: changeImpactTechnologyRecoverPlanFixed,
    shouldRender: !isOloCrq,
    shouldValidate: !isOloCrq,
    overwriteSubmit: {
      overwrite: isOloCrq,
      overwriteValue: 'Yes',
    },
    readOnly: hasCrqId,
  });
  const technologyPlanRequiredFixed = 'technology-plan-required-fixed';

  const technologyPlanRequiredFixedValue = !isOloCrq && values[technologyPlanRequiredFixed] ? values[technologyPlanRequiredFixed][0] : '';

  if (technologyPlanRequiredFixedValue !== 'Yes' && !isOloCrq) {
    riskQuestionnaireFields.push({
      ...defaultFieldLogic,
      componentData: whyNoChangeInTechnologyRecoveryPlansFixed,
      readOnly: hasCrqId,
      shouldRender: technologyPlanRequiredFixedValue === 'No',
    });
  }

  return riskQuestionnaireFields;
};



const getPreImplentationFields = (params) => {
  const {
    values = {},
    noValues,
    selectedNetwork,
    isOloCrq,
  } = params;

  const preImplentationFields = [];

  // MOBILE doens't have these fields
  if (selectedNetwork === MOBILE) {
    return preImplentationFields;
  }

  preImplentationFields.push({
    ...defaultFieldLogic,
    componentData: sstPreImplentationQuestionnaire,
    shouldRender: !isOloCrq,
    shouldValidate: !isOloCrq,
  });
  preImplentationFields.push({
    ...defaultFieldLogic,
    componentData: backoutAndTestCompleted,
    shouldRender: !isOloCrq,
    shouldValidate: !isOloCrq,
    overwriteSubmit: {
      overwrite: isOloCrq,
      overwriteValue: '10',
    },
  });
  preImplentationFields.push({
    ...defaultFieldLogic,
    componentData: customerImpacting,
    shouldRender: !isOloCrq,
    shouldValidate: !isOloCrq,
    overwriteSubmit: {
      overwrite: isOloCrq,
      overwriteValue: '10',
    },
  });
  preImplentationFields.push({
    ...defaultFieldLogic,
    componentData: affectsInventoryCmdbOss,
    shouldRender: !isOloCrq,
    shouldValidate: !isOloCrq,
    overwriteSubmit: {
      overwrite: isOloCrq,
      overwriteValue: '20',
    },
  });

  // we never show this field to the client and it's always set to it's initial value of true
  preImplentationFields.push({
    ...defaultFieldLogic,
    componentData: provideRequiredLeadTime,
    shouldRender: false,
    shouldValidate: false,
    overwriteSubmit: {
      overwrite: isOloCrq,
      overwriteValue: '10',
    },
  });

  preImplentationFields.push({
    ...defaultFieldLogic,
    componentData: requiresPreChangeLabTestingResults,
    shouldRender: !isOloCrq,
    shouldValidate: !isOloCrq,
    overwriteSubmit: {
      overwrite: isOloCrq,
      overwriteValue: '20',
    },
  });
  preImplentationFields.push({
    ...defaultFieldLogic,
    componentData: requiresNetworkItSecurityApproval,
    shouldRender: !isOloCrq,
    shouldValidate: !isOloCrq,
    overwriteSubmit: {
      overwrite: isOloCrq,
      overwriteValue: '20',
    },
  });

  let showWhyNoNetworkItSecurityApproval = false;

  if (!isOloCrq) {
    showWhyNoNetworkItSecurityApproval = noValues ? false : values[requiresNetworkItSecurityApproval.id].includes(preImp6No);
  }

  preImplentationFields.push({
    ...defaultFieldLogic,
    componentData: whyNoNetworkItSecurityApproval,
    shouldRender: showWhyNoNetworkItSecurityApproval,
    shouldValidate: showWhyNoNetworkItSecurityApproval,
    overwriteSubmit: {
      // we maintain the value in a formik level even if the field is not shown. This means they can switch between showing this
      // field with data they have entered. However the data they entered isn't saved to redux if the component isn't rendered
      // at the time they press submit (which is desired);
      overwrite: !showWhyNoNetworkItSecurityApproval || isOloCrq,
      overwriteValue: isOloCrq ? '20' : null,
    },
  });

  return preImplentationFields;
};


const getCurrentFields = (params) => {
  const currentFields = [];
  const {
    values = {},
    formData = {},
    // touched = {},
    userRoles,
  } = params;

  // this is only ever true on the very first render of the component if there is no
  // data in redux
  // const isOloCrq = getIsOlaCrq(formData);
  const changeDetails = 'CHANGE_DETAILS';
  const isOloByService = formData.dataByFormSectionId[changeDetails] ? formData.dataByFormSectionId[changeDetails].dataByFieldId.services.value === 'Network: Other Licensed Operator (OLO)' || formData.dataByFormSectionId[changeDetails].dataByFieldId.services.value === 'Network: International Other Licensed Operator (OLO)' : null;
  const isOloCrq = getIsOlaCrq(formData) || isOloByService;
  const noValues = isEmpty(values);
  const hasCrqId = formData.crqId ? true : false;
  const selectedNetwork = currentSelectedNetwork(formData);
  const userRole = userRoleForSelectedNetwork(formData, userRoles);
  const isExpedited = isCrqExpedited(formData);
  const createNetwork = formData.dataByFormSectionId.CHANGE_TYPE.dataByFieldId.select_network.displayValues[0].value;
  const hasCrqNumber = formData.crqId ? true : false;
  window.sessionStorage.setItem('createNetowrk', createNetwork, hasCrqNumber);

  let timingFields = [];
  let expeditedQuestions = [];
  let riskQuestionnaireQuestions = [];
  let preImplentationFields = [];

  // if (!isOloCrq) {

  timingFields = getTimingFields({
    selectedNetwork,
    userRole,
    isOloCrq,
    hasCrqNumber,
    values,
  });

  expeditedQuestions = getExpeditedFields({
    values,
    selectedNetwork,
    userRole,
    isOloCrq,
    isExpedited,
  });

  // gets the riskQuestionnaireQuestions for either MOBILE or FIXED
  riskQuestionnaireQuestions = selectedNetwork === MOBILE ? getMobileRiskQuestionnaireFields({
    values,
    noValues,
    isOloCrq,
    hasCrqId,
  }) : getFixedRiskQuestionnaireFields({
    values,
    noValues,
    isOloCrq,
    hasCrqId,
  });

  preImplentationFields = getPreImplentationFields({
    values,
    noValues,
    selectedNetwork,
    isOloCrq,
  });

  // Pushes all the fields into the
  currentFields.push(...timingFields);
  currentFields.push(...expeditedQuestions);
  // }
  if (selectedNetwork === FIXED) {
    const impactfields = getImpactFields({
      selectedNetwork,
      userRole,
      hasCrqId,
      isOloCrq,
      values,
    });

    currentFields.push({
      ...defaultFieldLogic,
      componentData: sstImpactLevel,
    });
    currentFields.push(...impactfields);
    // currentFields.push({
    //   ...defaultFieldLogic,
    //   componentData: impact,
    //   readOnly: hasCrqId,
    // });
  }
  // if (!isOloCrq) {
  currentFields.push(...riskQuestionnaireQuestions);
  currentFields.push(...preImplentationFields);
  // }
  return currentFields;
};

// generates the formSection!
const SectionTwo = React.memo(({
  handleSuccessfulSubmit,
  userRoles,
  formData,
  navigationProps,
  sectionIndex,
  editingStatus,
}) => {
  useEffect(() => {
    // setting the holiday on setion two before render
    const currentNetwork = formData.dataByFormSectionId.CHANGE_TYPE.dataByFieldId.select_network.displayValues[0].value || '';
    const isOLOInternational = formData.dataByFormSectionId.CHANGE_TYPE.dataByFieldId.olo_req_international_services.displayValues[0].value || '';
    if ((isOLOInternational !== 'Yes') && (currentNetwork === FIXED || currentNetwork === MOBILE || currentNetwork === FIXEDOLO)) {
      moment.updateLocale('en', {
        holidays: holidaysList,
        holidayFormat: 'MM-DD-YYYY',
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Props for the SectionNav in FormSection. these come from the parent but we mutate them to be relivent to the section
  const filteredNavigationProps = {
    ...navigationProps,
    submitForm: null,
  };

  const formattedSectionTitle = prefixStringWithIndexPlusOne(sectionTitle, sectionIndex);

  return (
    <FormSection
      sectionTitle={formattedSectionTitle}
      sectionId={sectionId}
      getCurrentFields={getCurrentFields}
      userRoles={userRoles}
      formData={formData}
      navigationProps={filteredNavigationProps}
      handleSuccessfulSubmit={handleSuccessfulSubmit}
      editingStatus={editingStatus}
    />
  );
});



SectionTwo.propTypes = {
  handleSuccessfulSubmit: PropTypes.func,
  userRoles: userRolesPropShape,
  formData: formDataShape,
  navigationProps: navigationPropsShape,
  sectionIndex: PropTypes.number,
  editingStatus: PropTypes.string,
};


export {
  SectionTwo,
  getCurrentFields,
  CHANGE_QUESTIONS,
};
